import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema } from '../Header';

import PeriodChart from './PeriodChart';

const schema: JSONSchema7 = {
    title: 'Periods Chart',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        chartTitles: headingsSchema,
        dataKey: {
            type: 'string',
            title: 'Data Key',
            default: '',
        },
        useSampleData: {
            type: 'boolean',
            title: 'Use Sample Data',
            default: true,
        },
        isPeriodType: {
            type: 'boolean',
            title: 'Select Period Type',
            default: false,
        },
        transformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        ...customSchemas.highchartOptionsSchema.HighchartOptionsSchema.schema(
            'Period Chart Options'
        ),
        plotLinesBuffer: {
            type: 'array',
            title: 'Plot Lines for Buffer Chart',
            items: {
                type: 'object',
                properties: {
                    value: { type: 'string', title: 'Value' },
                    color: { type: 'string', title: 'Color' },
                    width: { type: 'number', title: 'Width' },
                    label: {
                        type: 'object',
                        title: 'Label properties',
                        properties: {
                            text: { type: 'string', title: 'Text' },
                            align: { type: 'string', title: 'Align' },
                            x: { type: 'number', title: 'X' },
                            y: { type: 'number', title: 'Y' },
                            style: { type: 'string', title: 'Style Options as JSON string' },
                        },
                    },
                    dashStyle: { type: 'string', title: 'Dash Style' },
                    zIndex: { type: 'number', title: 'Z-Index' },
                },
            },
        },
        plotBandsBuffer: {
            type: 'array',
            title: 'Plot Bands for Buffer Chart',
            items: {
                type: 'object',
                properties: {
                    from: { type: 'string', title: 'From' },
                    to: { type: 'string', title: 'To' },
                    color: { type: 'string', title: 'Color' },
                },
            },
        },
        plotLinesAccelerator: {
            type: 'array',
            title: 'Plot Lines for Accelerator Chart',
            items: {
                type: 'object',
                properties: {
                    value: { type: 'string', title: 'Value' },
                    color: { type: 'string', title: 'Color' },
                    width: { type: 'number', title: 'Width' },
                    dashStyle: { type: 'string', title: 'Dash Style' },
                    zIndex: { type: 'number', title: 'Z-Index' },
                    label: {
                        type: 'object',
                        title: 'Label properties',
                        properties: {
                            text: { type: 'string', title: 'Text' },
                            align: { type: 'string', title: 'Align' },
                            x: { type: 'number', title: 'X' },
                            y: { type: 'number', title: 'Y' },
                            style: { type: 'string', title: 'Style Options as JSON string' },
                        },
                    },
                },
            },
        },
        plotBandsAccelerator: {
            type: 'array',
            title: 'Plot Bands for Accelerator Chart',
            items: {
                type: 'object',
                properties: {
                    from: { type: 'string', title: 'From' },
                    to: { type: 'string', title: 'To' },
                    color: { type: 'string', title: 'Color' },
                },
            },
        },
        toolTipBufferOptions: {
            type: 'array',
            title: 'Tooltip Buffer Options',
            items: {
                properties: {
                    label: { type: 'string', title: 'Label' },
                    mappingkey: { type: 'string', title: 'Mapping Key' },
                    format: { type: 'string', title: 'Format' },
                    className: { type: 'string', title: 'Class Name' },
                },
            },
        },
        toolTipAcceleratorOptions: {
            type: 'array',
            title: 'Tooltip Accelerator Options',
            items: {
                properties: {
                    label: { type: 'string', title: 'Label' },
                    mappingkey: { type: 'string', title: 'Mapping Key' },
                    format: { type: 'string', title: 'Format' },
                    className: { type: 'string', title: 'Class Name' },
                },
            },
        },
        paletteId: {
            type: 'string',
            title: 'Palette ID',
            default: 'pieChartPalette',
        },
        acceleratedZoneText: {
            type: 'string',
            title: 'Accelerated Zone Text',
            default: 'Accelerated Zone',
        },
        min: {
            type: 'string',
            title: 'Y-axis min range',
        },
        max: {
            type: 'string',
            title: 'Y-axis max range',
        },
    },
    dependencies: {
        transformData: {
            oneOf: [
                {
                    properties: {
                        transformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default: 'return (data) => { return data }',
                        },
                    },
                },
                {
                    properties: {
                        transformData: { enum: [false] },
                    },
                },
            ],
        },
        isPeriodType: {
            oneOf: [
                {
                    properties: {
                        isPeriodType: { enum: [true] },
                        functionToSelectPeriod: {
                            type: 'string',
                            description: 'Function to select theme',
                            default: `return (data) => { const period = 'buffer'; return period; }`,
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'paletteId',
            'chartTitles',
            'dataKey',
            'isPeriodType',
            'functionToSelectPeriod',
            'useSampleData',
            'options',
            'plotLinesBuffer',
            'plotBandsBuffer',
            'plotLinesAccelerator',
            'plotBandsAccelerator',
            'toolTipBufferOptions',
            'toolTipAcceleratorOptions',
            'acceleratedZoneText',
            'min',
            'max',
        ],
        'Transform Data': ['transformData', 'functionToTransform'],
    },
];

const uiSchema = {
    'ui:groups': groups,
    ...customSchemas.textSchema.TextSchema.uiSchema,
    ...customSchemas.styleSchema.StyleSchema.uiSchema,
    ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
    styles: { 'ui:widget': 'CSSEditor' },
    functionToTransform: { 'ui:widget': 'JSEditor' },
    chartTitles: {
        items: {
            headerText: {
                'ui:widget': 'ResourcePicker',
            },
        },
    },
    paletteId: { 'ui:widget': 'PaletteSelect' },
    dataKey: { 'ui:widget': 'CustomTextInput' },
    useSampleData: { 'ui:widget': 'CustomCheckbox' },
    isPeriodType: { 'ui:widget': 'CustomCheckbox' },
    functionToSelectPeriod: { 'ui:widget': 'JSEditor' },
    transformData: { 'ui:widget': 'CustomCheckbox' },
    plotLinesBuffer: {
        items: {
            value: { 'ui:widget': 'CustomTextInput' },
            color: { 'ui:widget': 'CustomTextInput' },
            width: { 'ui:widget': 'CustomTextInput' },
            dashStyle: { 'ui:widget': 'CustomTextInput' },
            zIndex: { 'ui:widget': 'CustomTextInput' },
            label: {
                text: { 'ui:widget': 'ResourcePicker' },
                align: { 'ui:widget': 'CustomTextInput' },
                x: { 'ui:widget': 'CustomTextInput' },
                y: { 'ui:widget': 'CustomTextInput' },
                style: { 'ui:widget': 'CustomTextInput' },
            },
        },
    },
    plotBandsBuffer: {
        items: {
            from: { 'ui:widget': 'ResourcePicker' },
            to: { 'ui:widget': 'ResourcePicker' },
            color: { 'ui:widget': 'CustomTextInput' },
        },
    },
    plotLinesAccelerator: {
        items: {
            value: { 'ui:widget': 'CustomTextInput' },
            color: { 'ui:widget': 'CustomTextInput' },
            width: { 'ui:widget': 'CustomTextInput' },
            dashStyle: { 'ui:widget': 'CustomTextInput' },
            zIndex: { 'ui:widget': 'CustomTextInput' },
            label: {
                text: { 'ui:widget': 'ResourcePicker' },
                align: { 'ui:widget': 'CustomTextInput' },
                x: { 'ui:widget': 'CustomTextInput' },
                y: { 'ui:widget': 'CustomTextInput' },
                style: { 'ui:widget': 'CustomTextInput' },
            },
        },
    },
    plotBandsAccelerator: {
        items: {
            from: { 'ui:widget': 'CustomTextInput' },
            to: { 'ui:widget': 'CustomTextInput' },
            color: { 'ui:widget': 'CustomTextInput' },
        },
    },
    toolTipBufferOptions: {
        items: {
            label: { 'ui:widget': 'ResourcePicker' },
            mappingkey: { 'ui:widget': 'CustomTextInput' },
            format: { 'ui:widget': 'CustomTextInput' },
            className: { 'ui:widget': 'CustomTextInput' },
        },
    },
    toolTipAcceleratorOptions: {
        items: {
            label: { 'ui:widget': 'ResourcePicker' },
            mappingkey: { 'ui:widget': 'CustomTextInput' },
            format: { 'ui:widget': 'CustomTextInput' },
            className: { 'ui:widget': 'CustomTextInput' },
        },
    },
    acceleratedZoneText: { 'ui:widget': 'ResourcePicker' },
    min: { 'ui:widget': 'CustomTextInput' },
    max: { 'ui:widget': 'CustomTextInput' },
    ...customSchemas.highchartOptionsSchema.HighchartOptionsSchema.uiSchema,
};

const PeriodChartWidget: commonTypes.IWidget = {
    schema,
    uiSchema,
    templateView: PeriodChart,
    id: 'PeriodChart',
    group: 'charts',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default PeriodChartWidget;
